import { ref, reactive, type Ref } from "vue";
import type {
  ShopListInput,
  Chain,
  ShopResponse,
  ChainPaymentMethod,
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
  DeliveryArea,
} from "@/generated/graphql";
import { ListDeliveryAreaDocument } from "@/generated/graphql";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { getShopList } from "@/common/graphql/shop";
import { getChainPaymentMethod } from "@/common/graphql/chain";
import { localStorageGetCustomerCode } from "@/common/localStorageUtil";
import type { DeliveryAreaInput, ChainSelectValue, ShopSelectValue, AreaSelectValue, AreaListType, UnassignedSection, AssignedSection } from "../types";

/**
 * composableへ渡す依存をまとめた型。
 */
interface UseAreaListFilterOptionsParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  deliveryAreaInput: DeliveryAreaInput;
  paymentMethodItems: Ref<ChainPaymentMethod[]>;
  listAreaSectionUnassigned: Ref<UnassignedSection>;
  listAreaSectionAssigned: Ref<AssignedSection>;
  displayConfirmDialogSectionUnassigned: Ref<boolean>;
  displayConfirmDialogSectionAssigned: Ref<boolean>;
  selectListCheckboxSectionAssigned: Ref<string[]>;
  listDataTotalCount: Ref<number>;
  displayPagingRangeUnassigned: Ref<string>;
  listDataTotalCountSectionAssigned: Ref<number>;
  displayPagingRangeAssigned: Ref<string>;
  searchConditionZipCode: Ref<string | null>;
  searchConditionZipCodeSectionAssigned: Ref<string | null>;
}

/**
 * 画面ロジックを扱う composable。
 */
export const useAreaListFilterOptions = ({
  errdialog,
  errMessage,
  deliveryAreaInput,
  paymentMethodItems,
  listAreaSectionUnassigned,
  listAreaSectionAssigned,
  displayConfirmDialogSectionUnassigned,
  displayConfirmDialogSectionAssigned,
  selectListCheckboxSectionAssigned,
  listDataTotalCount,
  displayPagingRangeUnassigned,
  listDataTotalCountSectionAssigned,
  displayPagingRangeAssigned,
  searchConditionZipCode,
  searchConditionZipCodeSectionAssigned,
}: UseAreaListFilterOptionsParams) => {
  const chainListItems = ref<Chain[]>([]);
  const chainSelectValues = ref<ChainSelectValue>({
    chain_code: "",
    name: "",
  });

  const shopListItems = ref<ShopResponse[]>([]);
  const shopSelectValues = ref<ShopSelectValue>({
    shop_code: "",
    name: "",
  });

  const shopListInput = reactive<ShopListInput>({
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    limit: 10000,
    offset: 0,
  });

  const errorMessages = ref<string>("");

  // エリア一覧（Tab1用）
  const listArea = ref<(DeliveryArea | null | undefined)[]>([]);

  // 配達区画登録タブ用
  const areaListItems = ref<AreaListType>([]);
  const areaSelectValues = ref<AreaSelectValue>({
    area_code: "",
    area_name: "",
  });

  // 配達区画一覧タブ用
  const areaListItemsSectionAssigned = ref<AreaListType>([]);
  const areaSelectValuesSectionAssigned = ref<AreaSelectValue>({
    area_code: "",
    area_name: "",
  });

  const searchConditionAreaCode = ref<string | null>(null);

  // チェーン選択 @update:modelValue
  const onChangeChain = async (): Promise<void> => {
    try {
      await clearData();
      if (!chainSelectValues.value?.chain_code) return;

      shopListInput.chain_code = chainSelectValues.value.chain_code;
      deliveryAreaInput.chain_code = chainSelectValues.value.chain_code;

      shopListItems.value = await getShopList(shopListInput);
      await listAreaView();
      await onClickLotRegistTab();
      searchConditionAreaCode.value = "";

      const paymentList = await getChainPaymentMethod(
        localStorageGetCustomerCode(),
        shopListInput.chain_code
      );
      paymentMethodItems.value = (paymentList ?? []).filter((p): p is ChainPaymentMethod => p !== null && p !== undefined);
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "不明なエラーが発生しました。";
    }
  };

  // 店舗選択 @update:modelValue
  const onChangeShop = async (): Promise<void> => {
    // v-autocompleteのクリア時にnullが渡される場合があるため、nullチェックを追加
    if (!shopSelectValues.value || !shopSelectValues.value.shop_code) {
      // 店舗名が空の場合、エリア名も空にする
      areaSelectValues.value = { area_code: "", area_name: "" };
      areaListItems.value = [];
      areaSelectValuesSectionAssigned.value = { area_code: "", area_name: "" };
      areaListItemsSectionAssigned.value = [];
      // 一覧をリセット
      listArea.value = [];
      // 検索条件エリアコードをクリア
      searchConditionAreaCode.value = "";

      return;
    }
    //店舗コードを設定
    deliveryAreaInput.shop_code = shopSelectValues.value.shop_code;
    // 一覧をリセット
    listArea.value = [];
    // エリア名称の選択値をクリア（店舗変更時はエリアが変わるため）
    areaSelectValues.value = { area_code: "", area_name: "" };
    areaSelectValuesSectionAssigned.value = { area_code: "", area_name: "" };
    // 検索条件エリアコードをクリア
    searchConditionAreaCode.value = "";
    // エリア一覧取得
    await listAreaView();
    await onClickLotRegistTab();
  };

  // (配達区画登録)エリア選択 @update:modelValue
  const onChangeAreaAtReg = async (): Promise<void> => {
    // v-autocompleteのクリア時にnullが渡される場合があるため、nullチェックを追加
    if (!areaSelectValues.value || !areaSelectValues.value.area_code) {
      // エリアコードが空の場合、検索条件エリアコードも空にする
      searchConditionAreaCode.value = "";
    } else {
      // エリアコードが空でない場合、検索条件エリアコードに設定する
      searchConditionAreaCode.value = areaSelectValues.value.area_code;
    }
  };

  // (配達区画一覧)エリア選択 @update:modelValue
  const onChangeAreaAtList = async (): Promise<void> => {
    // v-autocompleteのクリア時にnullが渡される場合があるため、nullチェックを追加
    if (!areaSelectValuesSectionAssigned.value || !areaSelectValuesSectionAssigned.value.area_code) {
      // エリアコードが空の場合、検索条件エリアコードも空にする
      searchConditionAreaCode.value = "";
    } else {
      // エリアコードが空でない場合、検索条件エリアコードに設定する
      searchConditionAreaCode.value = areaSelectValuesSectionAssigned.value.area_code;
    }
  };

  // エリア一覧取得処理
  const listAreaView = async (): Promise<void> => {
    try {
      const apolloClient = await generateApolloClient();
      const result = await apolloClient.query<ListDeliveryAreaQuery, ListDeliveryAreaQueryVariables>({
        query: ListDeliveryAreaDocument,
        variables: {
          chain_code: chainSelectValues.value?.chain_code,
          customer_code: localStorageGetCustomerCode(),
          shop_code: shopSelectValues.value.shop_code,
        },
        fetchPolicy: "no-cache",
      });
      if (result.data.listDeliveryArea) {
        listArea.value = result.data.listDeliveryArea;
        listArea.value.forEach(v => {
          if (v) {
            const parts = v.acceptable_end_time.split(":");
            v.acceptable_end_time = parts[0].padStart(2, "0") + ":" + parts[1].padStart(2, "0");
          }
        });
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "不明なエラーが発生しました。";
    }
  };

  // エリア登録タブのクリア処理
  const clearSectionData = async (): Promise<void> => {
    areaListItems.value = [];
    searchConditionZipCode.value = null;
    displayConfirmDialogSectionUnassigned.value = false;
    listAreaSectionUnassigned.value = [];
    listDataTotalCount.value = 0;
    displayPagingRangeUnassigned.value = "";
    listDataTotalCountSectionAssigned.value = 0;
    displayPagingRangeAssigned.value = "";
    searchConditionZipCodeSectionAssigned.value = null;
    areaListItemsSectionAssigned.value = [];
    displayConfirmDialogSectionAssigned.value = false;
    listAreaSectionAssigned.value = [];
  };

  // データクリア
  const clearData = async (): Promise<void> => {
    deliveryAreaInput.area_code = "";
    deliveryAreaInput.shop_code = "";

    // ①各検索条件をクリア
    shopSelectValues.value = { shop_code: "", name: "" };
    // GraphQLの結果オブジェクトは読み取り専用の場合があるため、オブジェクト全体を置き換える
    areaSelectValues.value = { area_code: "", area_name: "" };
    areaSelectValuesSectionAssigned.value = { area_code: "", area_name: "" };
    // 検索条件エリアコードをクリア
    searchConditionAreaCode.value = "";

    // ②セレクトボックス向けの選択肢をクリア
    shopListItems.value = [];
    areaListItems.value = [];
    areaListItemsSectionAssigned.value = [];
    listArea.value = [];
    selectListCheckboxSectionAssigned.value = [];
    listAreaSectionAssigned.value = [];
    listAreaSectionUnassigned.value = [];
  };

  // 配達区画登録タブ切替
  const onClickLotRegistTab = async (tabNum?: number | null): Promise<void> => {
    if (tabNum != null) {
      // 検索条件「配達エリア」を初期化
      areaSelectValues.value = { area_code: "", area_name: "" };
      areaListItems.value = [];
      areaSelectValuesSectionAssigned.value = { area_code: "", area_name: "" };
      areaListItemsSectionAssigned.value = [];
    }
    const apolloClient = await generateApolloClient();
    // エリアデータ取得処理
    const listAreaResult = await apolloClient.query<ListDeliveryAreaQuery, ListDeliveryAreaQueryVariables>({
      query: ListDeliveryAreaDocument,
      variables: {
        chain_code: deliveryAreaInput.chain_code,
        customer_code: localStorageGetCustomerCode(),
        shop_code: shopSelectValues.value.shop_code,
      },
    });
    if (!areaListItems.value || areaListItems.value !== listAreaResult.data.listDeliveryArea) {
      await clearSectionData();
    }

    // 対応する配達エリアが存在する場合、参照したエリアセットで更新する
    if (listAreaResult.data.listDeliveryArea && listAreaResult.data.listDeliveryArea.length > 0) {
      areaListItems.value = listAreaResult.data.listDeliveryArea;
      areaListItemsSectionAssigned.value = listAreaResult.data.listDeliveryArea;
    }

    if (tabNum != null) {
      if (tabNum === 1) {
        // 配達区画登録タブを開いたとき
        await onChangeAreaAtReg();
      } else {
        // 配達区画一覧タブを開いたとき
        await onChangeAreaAtList();
      }
    }
  };

  return {
    chainListItems,
    chainSelectValues,
    shopListItems,
    shopSelectValues,
    shopListInput,
    errorMessages,
    listArea,
    areaListItems,
    areaSelectValues,
    areaListItemsSectionAssigned,
    areaSelectValuesSectionAssigned,
    searchConditionAreaCode,
    onChangeChain,
    onChangeShop,
    onChangeAreaAtReg,
    onChangeAreaAtList,
    listAreaView,
    clearData,
    onClickLotRegistTab,
  };
};
